import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  addToCart,
  clearCart as clearCartStorage,
  removeFromCart,
  updateQuantity,
} from '../../services/cartService';
import { CartItem } from '../../types/cart';

type CartFeedback = {
  id: number;
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info';
};

interface CartState {
  items: CartItem[];
  feedback: CartFeedback | null;
  feedbackSequence: number;
  isHydrated: boolean;
}

const initialState: CartState = {
  items: [],
  feedback: null,
  feedbackSequence: 0,
  isHydrated: false,
};

const mergeCartItems = (storedItems: CartItem[], currentItems: CartItem[]) => {
  let mergedItems = [...storedItems];

  currentItems.forEach((item) => {
    mergedItems = addToCart(item, mergedItems);
  });

  return mergedItems;
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    hydrateCart(state, action: PayloadAction<CartItem[]>) {
      if (!state.isHydrated && state.items.length > 0) {
        state.items =
          action.payload.length > 0 ? mergeCartItems(action.payload, state.items) : state.items;
      } else {
        state.items = action.payload;
      }
      state.isHydrated = true;
    },
    addItem(state, action: PayloadAction<CartItem>) {
      state.items = addToCart(action.payload, state.items);
      state.isHydrated = true;
      state.feedbackSequence += 1;
      state.feedback = {
        id: state.feedbackSequence,
        open: true,
        message: 'Товар добавлен в корзину',
        severity: 'success',
      };
    },
    removeItem(state, action: PayloadAction<string>) {
      state.items = removeFromCart(action.payload, state.items);
      state.isHydrated = true;
    },
    updateItemQuantity(state, action: PayloadAction<{ key: string; quantity: number }>) {
      state.items = updateQuantity(action.payload.key, action.payload.quantity, state.items);
      state.isHydrated = true;
    },
    clearCart(state) {
      state.items = clearCartStorage();
      state.feedback = null;
      state.isHydrated = true;
    },
    dismissCartFeedback(state) {
      state.feedback = null;
    },
    showCartError(state, action: PayloadAction<string>) {
      state.feedbackSequence += 1;
      state.feedback = {
        id: state.feedbackSequence,
        open: true,
        message: action.payload,
        severity: 'error',
      };
    },
    syncCartMetadata(
      state,
      action: PayloadAction<
        Array<{ key: string; sku?: string; itemSnapshot?: CartItem['itemSnapshot'] }>
      >,
    ) {
      for (const update of action.payload) {
        const item = state.items.find((i) => i.key === update.key);
        if (item) {
          if (update.sku !== undefined) {
            item.sku = update.sku;
          }
          if (update.itemSnapshot !== undefined) {
            item.itemSnapshot = update.itemSnapshot;
          }
        }
      }
    },
  },
});

export const {
  hydrateCart,
  addItem,
  removeItem,
  updateItemQuantity,
  clearCart,
  dismissCartFeedback,
  showCartError,
  syncCartMetadata,
} = cartSlice.actions;

export default cartSlice.reducer;
